import { create } from 'zustand';
import { completeNovaTurn } from '../services/assistant/novaGemini';
import { runNovaTurn, type GeminiContent, type NovaSheet } from '../services/assistant/novaLoop';
import { persistNovaDraft, searchNovaClients, searchNovaDossiers } from '../services/assistant/novaPersist';
import { emptyNovaSession, type NovaKind, type NovaSession } from '../services/assistant/novaSession';

export type NovaChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
};

interface NovaAgentState extends NovaSession {
  messages: NovaChatMessage[];
  pending: boolean;
  sheet: NovaSheet | null;
  send: (text: string) => Promise<void>;
  exitCreation: () => void;
}

function mid(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function historyOf(messages: NovaChatMessage[]): GeminiContent[] {
  return messages.map((message) => ({
    role: message.role === 'user' ? 'user' : 'model',
    parts: [{ text: message.text }],
  }));
}

export const useNovaAgentStore = create<NovaAgentState>((set, get) => ({
  ...emptyNovaSession(),
  messages: [],
  pending: false,
  sheet: null,
  exitCreation: () => set({ ...emptyNovaSession(), sheet: null, pending: false }),
  send: async (text) => {
    const trimmed = text.trim();
    const state = get();
    if (!trimmed || state.pending) return;
    const userMessage: NovaChatMessage = { id: mid(), role: 'user', text: trimmed };
    set({ pending: true, messages: [...state.messages, userMessage] });
    const session: NovaSession = {
      creation: state.creation,
      kind: state.kind,
      documentId: state.documentId,
      polarId: state.polarId,
      dossiers: state.dossiers,
      clients: state.clients,
      clientDossierId: state.clientDossierId,
    };
    try {
      const turn = await runNovaTurn(session, historyOf(state.messages), trimmed, {
        complete: completeNovaTurn,
        searchDossiers: searchNovaDossiers,
        searchClients: searchNovaClients,
        persistDraft: persistNovaDraft,
      }, state.sheet);
      const reply: NovaChatMessage = { id: mid(), role: 'assistant', text: turn.reply };
      set({
        ...turn.session,
        sheet: turn.sheet,
        pending: false,
        messages: [...get().messages, reply],
      });
    } catch (err) {
      const reply: NovaChatMessage = {
        id: mid(),
        role: 'assistant',
        text: err instanceof Error ? err.message : 'Nova injoignable.',
      };
      set({ pending: false, messages: [...get().messages, reply] });
    }
  },
}));

export type { NovaKind };
